import React, { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BookOpen, FileText, Upload, Eye, Download, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = { not_started: "Not Started", in_progress: "In Process", submitted: "Submitted — awaiting review", needs_attention: "Needs Attention", completed: "Completed" };

async function openBlob(url, download, name) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  if (download) { const a = document.createElement("a"); a.href = href; a.download = name || "document"; a.click(); }
  else window.open(href, "_blank");
}

// Candidate-portal task extras: Knowledge Center resources, required document submission, Mark Complete.
export function PortalTaskExtras({ taskId, onChanged }) {
  const [x, setX] = useState(null);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get(`/portal/tasks/${taskId}/extras`).then((r) => setX(r.data)).catch(() => setX(null));
  useEffect(() => { load(); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!x) return null;
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/portal/tasks/${taskId}/upload`, fd); toast.success("Document uploaded"); load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); onChanged?.(); } catch (err) { toast.error(apiError(err)); } };
  const view = async (r) => {
    if (r.kind === "text") { const { data } = await api.get(`/portal/resources/${r.source_id}`); setViewing(data); }
    else if (r.has_file) await openBlob(`/portal/resources/${r.source_id}/file`, false);
    else if (r.link_url) window.open(r.link_url, "_blank", "noopener");
  };
  return (
    <div className="space-y-5 mt-2" data-testid="portal-task-extras">
      <p className="sr-only" data-testid="portal-task-status">Status: {STATUS[x.status] || x.status}</p>
      {x.resources.length > 0 && (
        <div data-testid="portal-task-resources">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Helpful Resources</p>
          {x.resources.map((r) => (
            <div key={r.source_id} className="rounded-lg border border-slate-200 p-2.5 flex items-center gap-2 mb-1.5" data-testid={`portal-resource-${r.source_id}`}>
              <BookOpen className="w-4 h-4 text-brand shrink-0" />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-700">{r.title}</p>{r.summary && <p className="text-xs text-slate-500 line-clamp-2">{r.summary}</p>}</div>
              <Button size="sm" variant="outline" onClick={() => view(r)} data-testid={`portal-resource-view-${r.source_id}`}><Eye className="w-3.5 h-3.5 mr-1" />View</Button>
              {r.has_file && <Button size="sm" variant="ghost" onClick={() => openBlob(`/portal/resources/${r.source_id}/file?download=true`, true, r.title)} data-testid={`portal-resource-download-${r.source_id}`}><Download className="w-3.5 h-3.5" /></Button>}
            </div>
          ))}
          {viewing && <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto" data-testid="portal-resource-body">{viewing.body}</div>}
        </div>
      )}
      {x.submission && (
        <div data-testid="portal-task-documents">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Your Documents</p>
          {x.status === "submitted" && <p className="text-xs text-[#006090] mb-1" data-testid="portal-task-submitted-note">Submitted — waiting for review by your district team.</p>}
          {x.submission.instructions && <p className="text-xs text-slate-500 mb-1">{x.submission.instructions}</p>}
          {x.files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-sm py-1" data-testid={`portal-task-file-${f.id}`}>
              <FileText className="w-4 h-4 text-slate-400" /><span className="flex-1 truncate">{f.filename}</span>
              <span className="text-[11px] text-slate-400">Submitted {new Date(f.uploaded_at).toLocaleDateString()}</span>
              <button onClick={() => openBlob(`/portal/tasks/${taskId}/files/${f.id}`, false)} className="text-slate-400 hover:text-slate-700"><Eye className="w-4 h-4" /></button>
              {x.can_upload && x.status !== "completed" && <button onClick={() => act(() => api.delete(`/portal/tasks/${taskId}/files/${f.id}`), "Removed")} className="text-slate-400 hover:text-rose-600" data-testid={`portal-task-file-remove-${f.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {x.can_upload && x.files.length < x.submission.max_files && (
            <>
              <input ref={fileRef} type="file" className="hidden" onChange={upload} accept={x.submission.file_types.map((t) => `.${t}`).join(",")} data-testid="portal-task-upload-input" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="portal-task-upload"><Upload className="w-3.5 h-3.5 mr-1" />{x.status === "needs_attention" ? "Upload Replacement" : x.files.length ? "Upload another / Replace" : "Upload Document"}</Button>
              <p className="text-[11px] text-slate-400 mt-1">Accepted: {x.submission.file_types.join(", ").toUpperCase()}</p>
            </>
          )}
        </div>
      )}
      {x.can_complete && <Button size="sm" className="bg-brand hover:bg-brand-strong" onClick={() => act(() => api.post(`/portal/tasks/${taskId}/complete`), "Marked complete")} data-testid="portal-task-complete"><CheckCircle2 className="w-4 h-4 mr-1" />Mark Complete</Button>}
    </div>
  );
}

// Staff: submitted documents for a task — View/Download + Complete / Needs Attention.
export function StaffSubmissions({ taskId, onChanged }) {
  const [x, setX] = useState(null);
  const load = () => api.get(`/program-tasks/task/${taskId}/submissions`).then((r) => setX(r.data)).catch(() => setX(null));
  useEffect(() => { load(); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!x || !x.submission) return null;
  const review = async (decision) => {
    let candidate_message;
    if (decision === "needs_attention") {
      candidate_message = window.prompt("Message to the candidate (shown in their portal — do not include internal notes):", "");
      if (candidate_message === null) return;
    }
    try { await api.post(`/program-tasks/task/${taskId}/review`, { decision, candidate_message }); toast.success(decision === "complete" ? "Marked complete" : "Sent back: needs attention"); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-lg border border-slate-200 p-2.5 mt-1 space-y-1" data-testid={`staff-submissions-${taskId}`}>
      {x.files.length === 0 ? <p className="text-xs text-slate-400">No documents submitted yet.</p> : x.files.map((f) => (
        <div key={f.id} className="flex items-center gap-2 text-xs" data-testid={`staff-file-${f.id}`}>
          <FileText className="w-3.5 h-3.5 text-slate-400" /><span className="flex-1 truncate">{f.filename}</span>
          <span className="text-slate-400">{f.uploaded_by} · {new Date(f.uploaded_at).toLocaleDateString()}</span>
          <button onClick={() => openBlob(`/attachments/${f.id}/file`, false)} data-testid={`staff-file-view-${f.id}`}><Eye className="w-3.5 h-3.5" /></button>
          <button onClick={() => openBlob(`/attachments/${f.id}/file`, true, f.filename)} data-testid={`staff-file-download-${f.id}`}><Download className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {x.status === "submitted" && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => review("complete")} data-testid={`staff-review-complete-${taskId}`}>Complete</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => review("needs_attention")} data-testid={`staff-review-attention-${taskId}`}>Needs Attention</Button>
        </div>
      )}
    </div>
  );
}

export const _API = API;
